import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { MigrationMode } from '../types';

interface ConfirmMigrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceName: string;
  targetName: string;
  totalDocuments: number;
  mode: MigrationMode;
  onConfirm: () => void;
  isExecuting: boolean;
  databaseEnvironment?: string;
}

export function ConfirmMigrationDialog({
  open,
  onOpenChange,
  sourceName,
  targetName,
  totalDocuments,
  mode,
  onConfirm,
  isExecuting,
  databaseEnvironment,
}: ConfirmMigrationDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const isConfirmEnabled = confirmText === sourceName && !isExecuting;
  const verb = mode === 'copy' ? 'Copy' : 'Move';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Confirm {verb}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {databaseEnvironment && (
                <div className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold ${
                  databaseEnvironment === 'Production Database'
                    ? 'bg-red-100 text-red-800 border border-red-300'
                    : 'bg-green-100 text-green-800 border border-green-300'
                }`}>
                  <span className={`h-2 w-2 rounded-full ${
                    databaseEnvironment === 'Production Database' ? 'bg-red-500' : 'bg-green-500'
                  }`} />
                  {databaseEnvironment}
                </div>
              )}
              <p>
                You are about to <strong>{verb} {totalDocuments.toLocaleString()} documents</strong> from{' '}
                <strong>{sourceName}</strong> to <strong>{targetName}</strong>.
              </p>
              <p className="text-destructive font-medium">
                {mode === 'copy'
                  ? 'This action cannot be undone. New documents will be created in the target account. Re-running will create duplicates.'
                  : 'This action cannot be undone. All assets will be transferred to the target account.'}
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Type <strong>{sourceName}</strong> to confirm:
                </label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={sourceName}
                  disabled={isExecuting}
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isExecuting}>Cancel</AlertDialogCancel>
          <Button
            onClick={onConfirm}
            disabled={!isConfirmEnabled}
            variant="destructive"
          >
            {isExecuting ? 'Executing...' : 'Execute Migration'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
